import { z } from "zod";

import { GpuSlugEnum } from "../lib/gpu.models";

export type ControlTowerRecord = {
  name: string;
  displayName: string | null;
  phase: string;
  agentVersion: string | null;
  lastHeartbeat: Date | null;
  createdAt: Date;
};

export type CreateControlTowerResult = ControlTowerRecord & {
  /** The plaintext join code. Only returned at creation time. */
  joinCode: string;
};

export interface ControlTowerServiceInterface {
  listControlTowers(): Promise<ControlTowerRecord[]>;
  createControlTower(params: {
    name: string;
    displayName?: string;
  }): Promise<CreateControlTowerResult>;
}

/**
 * Sizes of a tower's Mooncake L3 store for one card: one store pod per RDMA
 * GPU node of that card, holding DRAM and offloading to the node's local disk,
 * each sized from its own node. Store pods read them at start.
 */
export const ControlTowerMooncakeStoreSchema = z.object({
  /**
   * DRAM each store pod holds pages in (Mooncake's global segment), % of its
   * node's allocatable memory. Suggested: 12 (about 480 GiB on a B300).
   */
  dramPercent: z.number().positive().max(100),
  /**
   * Local disk each store pod may fill, % of its node's local disk (the node
   * NVMe on CoreWeave). Suggested: 25. At most 25 for the master's key
   * budget (~1.2-1.4 KB per disk key): the master uses its whole node, and
   * us-west-10a's ~80 B300 nodes at 25% hold ~390M keys, ~470 GB of index.
   * The disk itself would allow more: kubelet image GC starts at 65% use,
   * beside LOTA's 3000 GiB cache and the images.
   */
  localDiskPercent: z.number().positive().max(25),
  /** Staging buffer for peer reads of local-disk pages. Suggested: 16. */
  localDiskReadBufferGib: z.number().int().positive(),
  /**
   * GPU card whose RDMA nodes get a store pod. Each card runs its own master:
   * a store must share one InfiniBand fabric with its master and the engines
   * that use it, and on CoreWeave the B300 fabric does not reach the B200
   * nodes some B300 clusters also have (every cross-fabric transfer fails).
   */
  cardSlug: GpuSlugEnum,
});
export type ControlTowerMooncakeStore = z.infer<
  typeof ControlTowerMooncakeStoreSchema
>;

/** A tower's Mooncake L3 stores, at most one per card; empty when it offers none. */
export const ControlTowerMooncakeStoresSchema = z
  .array(ControlTowerMooncakeStoreSchema)
  .refine(
    (stores) =>
      new Set(stores.map((store) => store.cardSlug)).size === stores.length,
    { message: "Each card can have only one Mooncake store" },
  );
